import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import { captureException } from '@sentry/browser';

import { useApp } from '../../../hooks';
import { useListTimelineEntries } from '../../../hooks/timeline/useListTimelineEntries';
import LoadMoreButton from '../../LoadMoreButton/LoadMoreButton';
import { TimelineEntry } from '../TimelineEntry/TimelineEntry';

export const Timeline: FunctionComponent = () => {
  const { t } = useTranslation('common', { useSuspense: false });
  const { timeline, loading, fetchNextPage, hasNextPage } = useTimeline();

  if (!timeline) return null;

  const { nodes = [] } = timeline;

  return (
    <div data-qa="timeline">
      {nodes.map(timelineEntry => (
        <TimelineEntry key={timelineEntry.id} timelineEntry={timelineEntry} />
      ))}
      {hasNextPage && (
        <LoadMoreButton
          loading={loading}
          onClick={fetchNextPage}
          trackingData={{
            action_name: 'click',
            subtype: 'button',
            event: 'click',
            event_value: 'show more',
            sub_section: 'timeline',
          }}
        >
          {t('common.showMore', 'Show more')}
        </LoadMoreButton>
      )}
    </div>
  );
};

function useTimeline() {
  const { t } = useTranslation('common', { useSuspense: false });
  const { showToast } = useApp();
  const { loading, data, fetchMore } = useListTimelineEntries();

  const pageInfo = data?.page?.timeline?.pageInfo;

  const fetchNextPage = async () => {
    try {
      await fetchMore({
        variables: { after: pageInfo?.endCursor },
      });
    } catch (err) {
      showToast({
        message: t('timeline.fetchNextPage.errorMessage'),
        type: 'warning',
        dismissAfter: 10000,
      });

      captureException(err);
    }
  };

  return {
    timeline: data?.page?.timeline,
    loading,
    fetchNextPage,
    hasNextPage: pageInfo?.hasNextPage,
  };
}
